import { defineStore } from 'pinia';
import { computed, reactive, shallowRef, toRefs, watch } from 'vue';
import { i18n } from '@/plugins/i18n';
import {
	setLocalStorageItem,
	getLocalStorageItem,
	removeLocalStorageItem,
	EStorageKey,
} from '@/utils/localStorage';
import { useUserService } from '@/api/usersService';
import { useTermsOfUseService } from '@/api/termsOfUseService';
import { identifyUser, resetGainsight } from '@/plugins/gainsight';
import { identifyCustomerIo, resetCustomerIo } from '@/plugins/customerio';
import { usePusher } from '@/plugins/pusher';
import { clearDataDogUser, setDataDogUser } from '@/plugins/datadog';
import { handleUserChannelWebSocketNotification } from '@/utils/websocket';
import { useRouter, type RouteRecordName } from 'vue-router';
import { cloneDeep, get } from 'lodash-es';
import type { ExchangeConversationMessage } from '@/types/conversations';
import { ProfileAccessService } from '@/types/users';
import type { ServiceProvider } from '@/types/serviceProviders';
import { canAccessRoute } from '@/router';
import { isImpersonating } from './authStore';
import { checkPermission } from '@/utils/permissions';
import { updateLDContext } from '@/plugins/launchdarkly';
import { hasLeaderboardRead } from '@/permissions/exchange';
import { adaptUser, isAdminRole } from '@/utils/users';
import {
	TRANSACTION_UNIT_TYPES,
	ROLES,
	type Role,
	type ApiV1,
	type ApiV2,
	type PermissionRule,
	PermissionRules,
} from '@teamworksdev/influencer/core';
import { getInfluencer } from '@/plugins/influencer';
import type { UserAxleProfile } from '@/types/teamworks';

interface UseUserStoreState {
	user: ApiV2.User | undefined;
	impersonatedByUser: ApiV2.User | undefined;
	userProfiles: ApiV1.MinimalUserProfile[] | undefined;
	profile: ApiV1.FullUserProfile | undefined;
	inflcrId: string | null;
	inflcrImpersonatedById: string | null;
	impersonatorProfile: ApiV1.FullUserProfile | undefined;
	maxAthletesPerBulkTransactionProposal: number;
	userAxleProfile: UserAxleProfile | undefined;
	serviceProvider: ServiceProvider | undefined;
}

export interface ReceivedConversationEvent {
	conversationUuid: string;
	message: ExchangeConversationMessage;
	senderName: string;
}

export const useUserStore = defineStore('user', () => {
	const DEFAULT_STATE = {
		user: undefined,
		impersonatedByUser: undefined,
		userProfiles: undefined,
		profile: undefined,
		inflcrId: getLocalStorageItem<string>('inflcr-profile-id'),
		inflcrImpersonatedById: getLocalStorageItem<string>('inflcr-impersonated-by-profile-id'),
		impersonatorProfile: undefined,
		maxAthletesPerBulkTransactionProposal: 275,
		userAxleProfile: undefined,
		serviceProvider: undefined,
	};

	const state = reactive<UseUserStoreState>(cloneDeep(DEFAULT_STATE));
	const router = useRouter();
	const inflcr = getInfluencer();

	const subscribeToUserChannel = (userUuid: string) => {
		const pusher = usePusher();
		const channel = pusher.subscribe(`private-${import.meta.env.VITE_AWS_REGION}.user.${userUuid}`);
		channel.bind_global(handleUserChannelWebSocketNotification);
	};

	const receivedConversationEvent = shallowRef<ReceivedConversationEvent>();

	const setUser = async (impersonating: boolean = false) => {
		const user = await inflcr.getUser({ bypassImpersonation: impersonating });
		if (!user) throw new Error('Unable to fetch user');
		state.user = user;
		if (!impersonating && !isImpersonating()) {
			subscribeToUserChannel(user.uuid);
		}
		setDataDogUser(`${import.meta.env.VITE_AWS_REGION}:${user.uuid}`);
		if (hasUserAcceptedLatestTermsOfUse.value) {
			await setUserProfiles(impersonating);
		}
	};

	const setUserProfiles = async (impersonating: boolean = false) => {
		state.userProfiles = await inflcr.getMinimalUserProfiles({ bypassImpersonation: impersonating });
	};

	const setUserWithDefaultProfile = async (impersonating: boolean = false) => {
		// Fetch user
		const user = await inflcr.getUser({ bypassImpersonation: impersonating });
		if (!user) throw new Error('Unable to fetch user');
		state.user = user;

		if (!impersonating && !isImpersonating()) {
			subscribeToUserChannel(user.uuid);
		}
		setDataDogUser(`${import.meta.env.VITE_AWS_REGION}:${user.uuid}`);

		// If TOU not accepted, stop here
		if (!hasUserAcceptedLatestTermsOfUse.value) {
			return { hasDefaultProfile: false, requiresTOU: true };
		}

		// Try local storage profile first
		if (state.inflcrId) {
			try {
				await setProfile(state.inflcrId, impersonating);
				return { hasDefaultProfile: true, requiresTOU: false };
			} catch (error) {
				console.warn('Cached profile invalid. Falling back to default.', error);
				removeLocalStorageItem(EStorageKey.INFLCR_ID);
				state.inflcrId = null;
			}
		}

		// Try to get default profile
		const defaultProfile = await inflcr.getDefaultProfile({ bypassImpersonation: impersonating });

		if (defaultProfile) {
			// Admin users should always see the profile chooser
			if (isAdminRole(defaultProfile.role_id)) {
				// Don't auto-select for admins - fetch all profiles for chooser
				await setUserProfiles(impersonating);
				return { hasDefaultProfile: false, requiresTOU: false };
			}

			// Set as current profile with all tracking updates
			state.profile = defaultProfile;
			await updateLDContext();
			setInflcrId(defaultProfile.uuid);

			if (state.user) {
				identifyUser(state.user, state.profile);
				identifyCustomerIo(state.user, state.profile);
			}
			setUserAxleProfile();

			// Don't fetch all profiles - will lazy-load when needed
			return { hasDefaultProfile: true, requiresTOU: false };
		}

		// No default profile - fetch all profiles for chooser
		await setUserProfiles(impersonating);
		return { hasDefaultProfile: false, requiresTOU: false };
	};

	const ensureUserProfiles = async (impersonating: boolean = false) => {
		if (!state.userProfiles) {
			await setUserProfiles(impersonating);
		}
	};

	const updateUser = (data: Partial<ApiV1.User>) => {
		if (!state.user) throw 'User not found';
		Object.assign(state.user, data);
	};

	const setInflcrId = (profileUuid: string) => {
		state.inflcrId = profileUuid;
		setLocalStorageItem<string>(
			EStorageKey.INFLCR_ID,
			profileUuid,
		);
	};

	const setInflcrImpersonatedById = (profileUuid: string) => {
		state.inflcrImpersonatedById = profileUuid;
		setLocalStorageItem<string>(
			EStorageKey.INFLCR_IMPERSONATED_BY_ID,
			profileUuid,
		);
	};

	const setImpersonatedUser = async () => {
		resetGainsight();
		resetCustomerIo();
		const user = await inflcr.getUser();
		if (!user) throw new Error('Unable to get current user');
		state.impersonatedByUser = state.user;
		if (!state.inflcrId) throw new Error('No impersonator ID found');
		setInflcrImpersonatedById(state.inflcrId);
		if (!state.inflcrImpersonatedById) throw new Error('No impersonator ID found');
		state.impersonatorProfile = await inflcr.getFullUserProfile(state.inflcrImpersonatedById, { bypassImpersonation: true });
		state.user = user;
		if (hasUserAcceptedLatestTermsOfUse.value) {
			await setUserProfiles();
		}
		state.profile = undefined;
		state.inflcrId = null;
		setDataDogUser(`${import.meta.env.VITE_AWS_REGION}:${user.uuid}`);
		removeLocalStorageItem('inflcr-profile-id');
	};

	const acceptTermsOfUse = async (version: string) => {
		const termsOfUseService = useTermsOfUseService();
		const status = await termsOfUseService.acceptTermsOfUse({ version });
		if (status !== 204) throw new Error();
		state.user!.terms_of_use.accepted = version;
	};

	const setProfile = async (uuid?: string, bypassImpersonation: boolean = false) => {
		const _uuid = uuid ?? state.inflcrId;
		if (!_uuid) throw new Error('No profile uuid provided');

		state.profile = await inflcr.getFullUserProfile(_uuid, { bypassImpersonation });

		await updateLDContext();

		setInflcrId(_uuid);
		if (state.user) {
			identifyUser(state.user, state.profile);
			identifyCustomerIo(state.user, state.profile);
		}
		setUserAxleProfile();
	};

	const setServiceProvider = (data: ServiceProvider | undefined) => {
		state.serviceProvider = data;
	};

	watch(() => state.profile, async () => {
		setServiceProvider(undefined);
	});

	const hasAppSwitcher = computed(() => {
		if (!state.profile) return false;

		const appSwitcherRoles: Role[] = [
			ROLES.TEAM_USER,
			ROLES.COMPLIANCE_OFFICER,
			ROLES.BRAND_USER,
			ROLES.AGENT,
		];

		return appSwitcherRoles.includes(state.profile.role_id);
	});

	const setUserAxleProfile = async () => {
		if (hasAppSwitcher.value) {
			const userService = useUserService({ bypassErrorToast: true });
			try {
				const userAxleProfile = await userService.getUserAxleProfile();
				state.userAxleProfile = userAxleProfile;
				mergeUserProfileLogoAndUserAxleProfileLogo(userAxleProfile);
			} catch (e) {
				console.warn(e);
				state.userAxleProfile = undefined;
			}
		} else {
			state.userAxleProfile = undefined;
		}
	};

	const mergeUserProfileLogoAndUserAxleProfileLogo = (appSwitcherProfile: UserAxleProfile) => {
		if (!appSwitcherProfile.navbar.imageUrl) return;
		const profile = state.userProfiles?.find((profile) => profile.uuid === appSwitcherProfile.profileUuid);
		switch (profile?.role_id) {
			case ROLES.TEAM_USER:
				profile.data.team.logo = appSwitcherProfile.navbar.imageUrl;
				break;
			case ROLES.COMPLIANCE_OFFICER:
				profile.data.school.logo = appSwitcherProfile.navbar.imageUrl;
				break;
			default:
				break;
		}
	};

	const unsetUser = () => {
		state.profile = undefined;
		state.user = undefined;
		state.userProfiles = undefined;
		state.impersonatedByUser = undefined;
		state.inflcrId = null;
		state.inflcrImpersonatedById = null;
		state.userAxleProfile = undefined;
		removeLocalStorageItem('inflcr-profile-id');
		removeLocalStorageItem('inflcr-impersonated-by-profile-id');
		clearDataDogUser();
		receivedConversationEvent.value = undefined;
	};

	const impersonatedByProfile = computed<ApiV1.FullUserProfile | undefined>(() => {
		return state.impersonatorProfile;
	});

	const updateDefaultUserProfile = async (profileUuid: string) => {
		await inflcr.setDefaultProfile(profileUuid);
		state.userProfiles?.forEach((profile) => {
			profile.is_default = profile.uuid === profileUuid;
		});
	};

	const refreshImpersonatedByUser = async () => {
		const userService = useUserService();
		const user = adaptUser(await userService.getUser(true));
		if (!user) throw new Error();
		state.impersonatedByUser = user;
		if (!state.inflcrImpersonatedById) throw new Error('No impersonator ID found');
		state.impersonatorProfile = await inflcr.getFullUserProfile(state.inflcrImpersonatedById, { bypassImpersonation: true });
	};

	const activeTeam = computed<ApiV1.UserProfileTeam | null>(() => {
		return get(state, 'profile.data.team', null);
	});

	const hasUserAcceptedLatestTermsOfUse = computed<boolean>(() => (
		state.user?.terms_of_use.accepted === state.user?.terms_of_use.latest
	));

	const profileAccess = computed(() => new ProfileAccessService(state.profile));

	async function profileCanAccessCurrentRoute() {
		const currentRoute = router.currentRoute.value;
		return await canAccessRoute(currentRoute.meta);
	};

	const profileDefaultRoute = computed<RouteRecordName>(() => {
		const defaultRoutesByRole: Record<Role, RouteRecordName> = {
			[ROLES.SUPER_ADMINISTRATOR]: 'NoAccess',
			[ROLES.ADMINISTRATOR]: 'NoAccess',
			[ROLES.CLIENT_SUCCESS]: 'NoAccess',
			[ROLES.BRAND_USER]: 'ExchangeAthletes',
			[ROLES.AGENT]: 'ExchangeTransactions',
			[ROLES.TEAM_USER]: profileAccess.value.hasContentHubWrite ? 'ContentHubDashboard' : 'ContentHubIndex',
			[ROLES.COMPLIANCE_OFFICER]: 'ComplianceDashboard',
		};
		return state.profile?.role_id ? defaultRoutesByRole[state.profile.role_id] : 'NoAccess';
	});

	const hasMultipleProfiles = computed<boolean>(() => {
		return (state.userProfiles?.length ?? 0) > 1;
	});

	const defaultProfile = computed(() => {
		return state.userProfiles?.find((profile) => profile.is_default);
	});

	const brandProfile = computed(() => {
		return state.profile?.role_id === ROLES.BRAND_USER ? state.profile : undefined;
	});

	const complianceOfficerSchool = computed(() => {
		return state.profile?.role_id === ROLES.COMPLIANCE_OFFICER ? state.profile.data.school : undefined;
	});

	const updateExchangeBrandProfile = (data: Partial<ApiV1.FullExchangeBrand>) => {
		if (state.profile?.role_id !== ROLES.BRAND_USER) {
			throw 'Brand profile not found';
		}
		Object.assign(state.profile.data.exchange_brand, data);
	};

	const entityType = computed<number | undefined>(() => {
		return brandProfile.value?.data.exchange_brand.entity_type ?? undefined;
	});

	const brandCanCreateBulletins = computed<boolean>(() => {
		if (!profileAccess.value.canAccessBulletins || !brandProfile.value) return false;
		const activeBulletinsAllowed = brandProfile.value.data.exchange_brand.active_bulletins_allowed;
		const activeBulletinsCount = brandProfile.value.data.exchange_brand.active_bulletins_count;
		return activeBulletinsAllowed === null || activeBulletinsCount < activeBulletinsAllowed;
	});

	const featuredBulletinsAllowed = computed<number>(() => {
		return brandProfile.value?.data.exchange_brand.max_number_of_featured_bulletins ?? 0;
	});

	const updateFeaturedBulletinsCount = (increment: boolean) => {
		const incrementValue = increment ? 1 : -1;
		if (brandProfile.value) brandProfile.value.data.exchange_brand.featured_bulletins_count += incrementValue;
	};

	const transactionsRouteName = computed<string>(() => {
		switch (state.profile?.role_id) {
			case ROLES.AGENT:
			case ROLES.BRAND_USER:
			case ROLES.TEAM_USER:
				return 'ExchangeTransactions';

			case ROLES.COMPLIANCE_OFFICER:
				return 'ComplianceTransactions';

			default:
				return 'NotFound';
		}
	});

	const transactionRouteName = computed<string>(() => {
		switch (state.profile?.role_id) {
			case ROLES.AGENT:
			case ROLES.BRAND_USER:
			case ROLES.TEAM_USER:
				return 'ExchangeTransaction';

			case ROLES.COMPLIANCE_OFFICER:
				return 'ComplianceTransaction';

			default:
				return 'NotFound';
		}
	});

	const agreementsRouteName = computed<string>(() => {
		switch (state.profile?.role_id) {
			case ROLES.AGENT:
			case ROLES.BRAND_USER:
			case ROLES.TEAM_USER:
				return 'ExchangeAgreements';

			case ROLES.COMPLIANCE_OFFICER:
				return 'ComplianceAgreements';

			default:
				return 'NotFound';
		}
	});

	const agreementRouteName = computed<string>(() => {
		switch (state.profile?.role_id) {
			case ROLES.AGENT:
			case ROLES.BRAND_USER:
			case ROLES.TEAM_USER:
				return 'ExchangeAgreement';

			case ROLES.COMPLIANCE_OFFICER:
				return 'ComplianceAgreement';

			default:
				return 'NotFound';
		}
	});

	const featuredBulletinsCount = computed<number>(() => {
		return brandProfile.value?.data.exchange_brand.featured_bulletins_count ?? 0;
	});

	const brandCanFeatureBulletins = computed<boolean>(() => {
		if (!profileAccess.value.canAccessBulletins || !brandProfile.value) return false;
		return featuredBulletinsCount.value < featuredBulletinsAllowed.value;
	});

	const maxAthletesPerBulkMessage = computed<number>(() => {
		return brandProfile.value?.data.exchange_brand.max_athletes_per_bulk_message ?? state.maxAthletesPerBulkTransactionProposal;
	});

	const paymentsReady = computed<boolean>(() => {
		if (!brandProfile.value) return false;
		return brandProfile.value.data.exchange_brand.payments_ready;
	});

	const isWalletVisible = computed<boolean>(() => {
		if (state.profile?.role_id === ROLES.COMPLIANCE_OFFICER) return true;

		if (!brandProfile.value) return false;
		return brandProfile.value.data.exchange_brand.is_wallet_visible;
	});

	const leaderboardEnabled = computed<boolean>(() => {
		if (checkUserPermission(PermissionRules.hasRole(ROLES.BRAND_USER))) {
			return brandProfile.value!.data.exchange_brand.leaderboard.is_enabled;
		} else if (checkUserPermission(PermissionRules.hasRole(ROLES.AGENT))) {
			return checkUserPermission(hasLeaderboardRead);
		}
		return false;
	});

	const leaderboardUsesPoints = computed<boolean>(() => {
		if (checkUserPermission(PermissionRules.hasRole(ROLES.BRAND_USER))) {
			return brandProfile.value!.data.exchange_brand.leaderboard.unit_type === TRANSACTION_UNIT_TYPES.POINTS;
		} else if (checkUserPermission(PermissionRules.hasRole(ROLES.AGENT))) {
			return checkUserPermission(hasLeaderboardRead);
		}
		return false;
	});

	const newAgreementText = computed(() => {
		return !brandProfile.value || leaderboardEnabled.value
			? i18n.global.t('proposeTransaction')
			: i18n.global.t('proposeAgreement');
	});

	const checkUserPermission = (rule: PermissionRule): boolean => {
		try {
			return checkPermission(state.profile, rule);
		} catch (error) {
			console.error('[User store] Permission check failed:', error);
			return false;
		}
	};

	const $reset = () => {
		Object.assign(state, cloneDeep(DEFAULT_STATE));
	};

	return {
		...toRefs(state),
		setUser,
		setUserProfiles,
		setUserWithDefaultProfile,
		ensureUserProfiles,
		updateUser,
		setImpersonatedUser,
		setProfile,
		setServiceProvider,
		setInflcrId,
		unsetUser,
		acceptTermsOfUse,
		activeTeam,
		hasUserAcceptedLatestTermsOfUse,
		profileAccess,
		profileDefaultRoute,
		profileCanAccessCurrentRoute,
		impersonatedByProfile,
		refreshImpersonatedByUser,
		subscribeToUserChannel,
		receivedConversationEvent,
		brandProfile,
		entityType,
		brandCanCreateBulletins,
		brandCanFeatureBulletins,
		maxAthletesPerBulkMessage,
		featuredBulletinsAllowed,
		featuredBulletinsCount,
		updateFeaturedBulletinsCount,
		transactionsRouteName,
		transactionRouteName,
		agreementsRouteName,
		agreementRouteName,
		paymentsReady,
		updateExchangeBrandProfile,
		isWalletVisible,
		leaderboardEnabled,
		leaderboardUsesPoints,
		newAgreementText,
		updateDefaultUserProfile,
		defaultProfile,
		hasMultipleProfiles,
		checkUserPermission,
		complianceOfficerSchool,
		$reset,
	};
});
